/*
 * Developer  : Sakshi Bhise
 * Script     : SidebarLayout.tsx
 * Date       : 2026-06-08
 * Purpose    : The root layout shell for the entire AskHR app. It renders the
 *              collapsible sidebar, all secondary panels (chat history, settings,
 *              support mode), the first-time consent popup, toast notifications,
 *              and hands off the centre area to whichever child page is active.
 */

// ─── Imports ──────────────────────────────────────────────────────────────────

import { COLORS, DisclaimerPopup, DropdownOption, FeedbackDialog, SearchOption, Sidebar, SidebarLayout as TevaSidebarLayout, useSidebar } from "@teva/ui"; // Teva design-system: colour tokens, the consent popup, the sidebar shell, and more
import { useCallback, useContext, useEffect, useRef, useState } from "react";  
import { createPortal } from "react-dom";                                                                                // React tools: useCallback/useMemo cache functions; useContext reads shared state; useEffect/useRef manage side effects and stable references; useState tracks changing values
import { Outlet } from "react-router-dom";                                                                                                                  // Renders whichever child route is currently active (e.g. the Chat page)
import Cookies from "js-cookie";                                                                                                                            // Reads and writes browser cookies — used to remember whether the sidebar is pinned
import { ToastContainer, toast } from "react-toastify";                                                                                                    // Shows small pop-up notification banners (e.g. "Support started for user@teva.com")
import "react-toastify/dist/ReactToastify.css";                                                                                                             // Default styles for the toast notification banners
import Teva from "../../assets/askhr-favi.png";                                                                                                             // Teva logo image shown in the sidebar header
import {
    useTheme, mergeStyles,
    Dialog, DialogFooter, DialogType,
    PrimaryButton, DefaultButton,
} from "@fluentui/react";                                                                                                                                   // Fluent UI: theme colours, CSS helpers, and pre-built dialog + button components
import { SidebarNavItems, navItems } from "./SidebarNavItems";                                                                                              // The icon navigation items rendered in the sidebar body
import { SettingsPanel, LanguagePanel, getFooterMenuItems } from "./SidebarFooterMenu";                                                                     // The Settings and Language panels, plus the footer menu builder
import { getSidebarLayoutStyles } from "./Sidebarlayout.style";                                                                                             // All CSS-in-JS style rules for this layout
import { ChatHistoryPanel } from "../ChatHistory/ChatHistoryPanel";                                                                                         // The full conversation history list shown in the secondary panel
import { AppStateContext } from "../../state/AppProvider";                                                                                                  // Global app state: logged-in user, language, chat history, permissions, etc.
import { AuthContext } from "../../state/AuthContext";                                                                                                      // Authentication context — provides the user's access token for API calls
import { acceptUserConsent, getUserInfo, impersonateUser, logEvent, SQLDBStatus, updateLanguage } from "../../api";                                         // API helper functions: consent, user info, impersonation, event logging, language preference
import { useTranslation } from "react-i18next";                                                                                                             // Hook that returns translated text for the currently active language
import i18n from "../../i18n";                                                                                                                              // Internationalisation engine — used here to detect RTL languages (Hebrew, Arabic)
import { SupportModePanel } from "../ImpersonateMenu";                                                                                                      // The panel support staff use to start/stop viewing the app as another user

// ─── Types & Interfaces ───────────────────────────────────────────────────────

// Shared data passed from this layout down to the child route (e.g. the Chat page).
// The child uses newChatRef to call the "start a new chat" action defined in this layout.
export type OutletContext = {
    newChatRef: React.MutableRefObject<(() => void) | null>;
    openTicketRef: React.MutableRefObject<(() => void) | null>;
};

// ─── Component: MobileDrawerCloser ────────────────────────────────────────────

/**
 * An invisible helper that closes the mobile sidebar drawer when asked to.
 * It lives inside the Sidebar so it can access the drawer's close function.
 * When the `trigger` number goes up by one, it fires the close — this is a
 * workaround for the sidebar library automatically re-opening the drawer
 * when a side panel closes.
 *
 * @param props
 * @param props.trigger - A counter; every time it increases, the mobile drawer is closed once.
 *   Example: starts at 0, incremented to 1 when the user opens a panel on mobile.
 * @returns Renders nothing visible on screen.
 */
function MobileDrawerCloser({ trigger }: { trigger: number }) {
    const { closeMobile } = useSidebar(); // the function to close the mobile drawer, provided by the Sidebar context

    // Store the close function in a ref so we always have the latest version without re-running the effect
    const closeMobileRef = useRef(closeMobile);
    closeMobileRef.current = closeMobile;

    useEffect(() => {
        if (trigger > 0) {
            // Use setTimeout(0) to let the current render finish before closing the drawer
            const id = setTimeout(() => closeMobileRef.current(), 0);
            return () => clearTimeout(id); // clean up the timer if the component unmounts before it fires
        }
    }, [trigger]); // intentionally omit closeMobile — ref keeps it current without re-triggering

    // Safe-area spacer: on iOS the home indicator bar sits at the bottom of the
    // screen. Without this spacer the sidebar footer (avatar) is partially hidden
    // behind the home indicator at 100% zoom in mobile Safari.
    return <div style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} />;
}

// ─── Component: SidebarLayout ─────────────────────────────────────────────────

// The main layout wrapper for the entire app. Renders once and stays on screen
// as the user navigates between pages. All panels, dialogs, and notifications
// are managed from here.
export default function SidebarLayout() {
    const { t } = useTranslation();                              // returns translated text for the current language
    const appStateContext = useContext(AppStateContext);          // reads and updates global app state
    const { token } = useContext(AuthContext);                   // the user's login token — required for authenticated API calls
    const theme = useTheme();                                    // current Fluent UI colour theme
    const styles = getSidebarLayoutStyles(theme);                // CSS-in-JS style rules built from the current theme

    const userLoggedIn = appStateContext?.state?.loggedInUser;   // the logged-in user's email address
    // Build the full display name; if the name is not available fall back to an empty string
    const userName = appStateContext?.state?.loggedInUserName ? appStateContext?.state?.loggedInUserName?.firstName + " " + appStateContext?.state?.loggedInUserName?.lastName : '';

    // ── Sidebar panel state ──

    const [activePanel, setActivePanel] = useState<string | null>(null);  // which secondary panel is currently open ('chat-history', 'support-mode', 'user-menu'), or null if none
    const [isPinned, setIsPinned] = useState(() => Cookies.get("askhr_sidebar_pinned") === "true"); // true when the sidebar should stay expanded; remembered in a cookie across sessions
    const [panelView, setPanelView] = useState<string | null>(null);      // which sub-view is shown inside the user-menu panel: 'Settings' or 'Languages'
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Tracks the latest isPinned value without causing effect re-runs
    const isPinnedRef = useRef(isPinned);
    isPinnedRef.current = isPinned;
    // Stores the pin state captured just before a secondary panel opens so it can be restored when the panel closes
    const pinnedBeforePanelRef = useRef(false);
    // Skips the panel effect on the very first render (activePanel starts null and we do not want to trigger pin logic at startup)
    const isPanelEffectFirstRun = useRef(true);

    // Saves the pinned preference to a 1-year cookie whenever it changes
    const handlePinnedChange = useCallback((pinned: boolean) => {
        setIsPinned(pinned);
        Cookies.set("askhr_sidebar_pinned", String(pinned), { expires: 365 }); // remember the choice for 365 days
    }, []);

    // ── Impersonation state (support staff only) ──

    const [settings, setSettings] = useState({ Email: "" });                                     // the email address typed into the impersonation form
    const [impersonationActive, setImpersonationActive] = useState(false);                       // true when a support agent is currently viewing the app as another user
    const [userDetails, setUserDetails] = useState({ email: "", country: "", workSite: "" });   // details of the user currently being impersonated
    const [error, setError] = useState<string | undefined>(undefined);                           // validation or server error message shown in the impersonation form

    // ── Consent dialog state ──

    const [isConsentDialogOpen, setConsentDialogOpen] = useState<boolean>(false); // true when the first-time terms-and-conditions popup should be visible

    const [isLoading, setLoading] = useState(false); // true while the "I Accept" button is waiting for the server to respond

    // ── New chat ref (shared with child routes via Outlet context) ──

    const newChatRef = useRef<(() => void) | null>(null);         // a stable reference to the "start new chat" function registered by the Chat page
    const openTicketRef = useRef<(() => void) | null>(null);      // a stable reference to the "open ticket dialog" function registered by the Chat page
    const loadingToastRef = useRef<string | number | null>(null); // keeps track of the in-progress toast so it can be updated when the operation finishes
    const handleNewChatClick = () => { newChatRef.current?.(); }; // triggers the new-chat action registered by the Chat page
    const handleOpenTicketClick = () => { openTicketRef.current?.(); }; // triggers the ticket dialog registered by the Chat page

    const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
        appStateContext?.state.userPreferredLanguage || appStateContext?.state.userLanguage // start with the saved preference, falling back to the auto-detected language
    );

    const isSupportUser = !!appStateContext?.state.isSupportUser; // true when the logged-in account has support/admin privileges

    // Sorts and converts the available languages into the format the dropdown expects — English first, the rest A–Z
    const dropdownOptions: SearchOption[] = (appStateContext?.state.availableLanguages || []).sort((a: any, b: any) => {
        if (a.code === 'en') return -1;
        if (b.code === 'en') return 1;
        return a.name.localeCompare(b.name);
    }).map((language: any) => ({
        key: language.code,        // short code, e.g. 'he'
        subLabel: language.native, // native name, e.g. 'עברית'
        label: language.name,      // English name, e.g. 'Hebrew'
    }));

    const currentLangKey = appStateContext?.state.userPreferredLanguage || appStateContext?.state.userLanguage; // the currently active language code
    const currentLangOption = dropdownOptions.find(opt => opt.key === currentLangKey);                          // the full dropdown entry for the active language
    const currentLanguageNativeLabel = currentLangOption?.subLabel || currentLangOption?.label || 'English';   // native name shown in the footer menu, e.g. 'עברית'

    // ── Effects ──

    // Keeps i18n in sync with the user's language from global state.
    // Also dismisses the "restoring preferences" loading toast once the language is confirmed.
    useEffect(() => {
        const lang = appStateContext?.state.userPreferredLanguage || appStateContext?.state.userLanguage;
        if (lang) i18n.changeLanguage(lang); // switch all translated text to the current language
        if (loadingToastRef.current !== null) {
            // Replace the loading spinner toast with a success message
            toast.update(loadingToastRef.current, {
                render: "Your preferences have been restored.",
                type: "success",
                isLoading: false,
                autoClose: 2000,
                closeOnClick: true,
            });
            loadingToastRef.current = null; // clear the ref so we do not try to update it again
        }
    }, [appStateContext?.state.userPreferredLanguage, appStateContext?.state.userLanguage]);

    
    // Keeps the actual <html> element's dir attribute in sync with the active language.
    // Some library components (Fluent Callout RTL detection, Sidebar.Footer's manual
    // menu positioning) read document.documentElement.dir directly rather than a prop,
    // so this must be set globally, not just passed as a local `dir` prop per component.
    useEffect(() => {
        const isRtlLang = i18n.language === "he" || i18n.language === "ar";
        document.documentElement.dir = isRtlLang ? "rtl" : "ltr";
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    // Converts a duration string (e.g. "30d", "2h") into a number of milliseconds.
    // Used to decide whether the consent popup should be shown again.
    // Example: "30d" → 2592000000 (30 days in ms)
    function parseDuration(duration?: string): number | null {
        if (!duration) return null;
        const match = duration.match(/^(\d+)(s|m|h|d)$/); // expect a number followed by s, m, h, or d

        if (!match) {
            throw new Error(`Invalid duration: ${duration}`);
        }

        const value = Number(match[1]); // the numeric part, e.g. 30
        const unit = match[2];          // the unit letter, e.g. 'd'

        // How many milliseconds each unit represents
        const multipliers = {
            s: 1000,
            m: 60 * 1000,
            h: 60 * 60 * 1000,
            d: 24 * 60 * 60 * 1000,
        };

        return value * multipliers[unit as keyof typeof multipliers];
    }

    // Checks whether the user needs to see the terms-and-conditions popup.
    // Shows it when: (a) the user has never accepted, or (b) the acceptance is older than the configured interval.
    useEffect(() => {
        // userEmployeeGroup is null until the user-details API call completes (same call that sets termsAcceptedAt).
        // Skipping here prevents the dialog from opening during the loading state when termsAcceptedAt is null
        // simply because the data hasn't arrived yet.
        if (appStateContext?.state.userEmployeeGroup == null) return;

        function checkConsent() {
            try {
                const consentInterval = parseDuration(appStateContext?.state.frontendSettings?.consent_interval);
                const termsAcceptedAt = appStateContext?.state.termsAcceptedAt;
                const now = Date.now();

                if (termsAcceptedAt == null) {
                    // User has never accepted — show the popup immediately
                    setConsentDialogOpen(true);
                } else {
                    const date = new Date(termsAcceptedAt).getTime();
                    if (consentInterval && (now - date >= consentInterval)) {
                        // Enough time has passed since the last acceptance — show the popup again
                        setConsentDialogOpen(true);
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }

        checkConsent();

        const consentInterval = parseDuration(appStateContext?.state.frontendSettings?.consent_interval);
        if (!consentInterval) return;

        // Re-check on the interval so the popup appears after the configured duration elapses mid-session.
        const timer = setInterval(checkConsent, consentInterval);
        return () => clearInterval(timer);
    }, [appStateContext?.state.userEmployeeGroup, appStateContext?.state.termsAcceptedAt, appStateContext?.state.frontendSettings?.consent_interval]);

    // When a secondary panel opens, temporarily un-pins the sidebar so it collapses
    // to give the panel more room. When the panel closes, the pin state is restored.
    useEffect(() => {
        if (isPanelEffectFirstRun.current) {
            isPanelEffectFirstRun.current = false; // skip on first render — activePanel is null and we do not want to reset the pin
            return;
        }
        if (activePanel) {
            pinnedBeforePanelRef.current = isPinnedRef.current; // remember whether the sidebar was pinned before the panel opened
            setIsPinned(false);                                  // collapse the sidebar to make room for the panel
        } else {
            const restored = pinnedBeforePanelRef.current;
            setIsPinned(restored);                               // restore the pin state when the panel closes
            Cookies.set("askhr_sidebar_pinned", String(restored), { expires: 365 });
        }
    }, [activePanel]);

    // Utility to detect if the text is in a right-to-left language  
    const isRTL = (text: string): boolean => {
        const rtlCharRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/; // Hebrew, Arabic, etc.  
        return rtlCharRegex.test(text);
    };


    /**
     * Closes whichever side panel is currently open and resets the panel view and title.
     * Called when the user clicks the × button in the panel header or opens the Prompt Library.
     *
     * @returns Nothing.
     */
    const closePanel = useCallback(() => {
        setActivePanel(null);  // no panel is open
        setPanelView(null);    // reset the sub-view title (Settings / Languages)
    }, []);

    // A counter that goes up by one each time we need to close the mobile drawer.
    // MobileDrawerCloser watches this number and fires closeMobile() when it changes.
    // This is needed because the sidebar library re-opens the mobile drawer whenever
    // the secondary panel closes — bumping this counter cancels that re-open.
    const [closeMobileTrigger, setCloseMobileTrigger] = useState(0);

    /**
     * Closes the side panel AND tells the mobile drawer to close at the same time.
     * Used specifically by the Chat History panel's close button, which is the only
     * panel that can be open while the mobile drawer is also visible.
     *
     * @returns Nothing.
     */
    const closePanelFromChatHistory = useCallback(() => {
        closePanel();
        setCloseMobileTrigger(prevTrigger => prevTrigger + 1); // increment triggers MobileDrawerCloser to fire closeMobile()
    }, [closePanel]);

    // ── Impersonation handlers ──

    const [isImpersonateLoading, setIsImpersonateLoading] = useState(false); // true while the impersonation API call is in progress

    // Called when the support agent clicks "Start Support Session".
    // Validates the email, calls the API, and updates global state with the impersonated user's data.
    const handleImpersonate = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!settings.Email.length) { setError("Email is Required"); return; }          // reject empty input
        if (!emailRegex.test(settings.Email)) { setError("Please Enter Valid Email"); return; } // reject malformed email

        setError(undefined); // clear any previous error message
        setIsImpersonateLoading(true);
        const toastId = toast.loading("Starting impersonation..."); // show a spinner toast while waiting
        try {
            const response = await impersonateUser(
                settings.Email,
                appStateContext?.state.loggedInUserCountry,
                appStateContext?.state.userGId,
                token,
            );
            if (response.ok) {
                const data = await response.json();
                setUserDetails({ email: data.email, country: data.country, workSite: data.location }); // store the impersonated user's details for display in the Support Mode panel
                setImpersonationActive(true);
                appStateContext?.dispatch({ type: "IS_IMPERSONATING", payload: true });
                appStateContext?.dispatch({ type: "IMP_USER_COUNTRY", payload: data.country });
                appStateContext?.dispatch({ type: "USER_PERMISSION", payload: data.permissions });

                if (data.languages) {
                    // Replace the language list with the impersonated user's available languages
                    appStateContext?.dispatch({ type: "SET_AVAILABLE_LANGUAGES", payload: data.languages });
                }
                appStateContext?.dispatch({ type: "USER_LANGUAGE", payload: data.language_code });
                appStateContext?.dispatch({ type: "IMPERSONATED_USER", payload: settings.Email });
                handleNewChatClick(); // start a fresh chat as the impersonated user
                toast.update(toastId, { render: `Support started for ${data.email}`, type: "success", isLoading: false, autoClose: 3000, closeOnClick: true });
            } else {
                const errorObj = await response.json();
                setError(errorObj.error || "An error occurred while impersonating.");
                toast.update(toastId, { render: errorObj.error || "An error occurred while impersonating.", type: "error", isLoading: false, autoClose: 3000, closeOnClick: true });
            }
        } catch {
            toast.update(toastId, { render: "Unexpected error occurred.", type: "error", isLoading: false, autoClose: 3000, closeOnClick: true });
        } finally {
            setIsImpersonateLoading(false); // re-enable the form whether the call succeeded or failed
        }
    };

    // Clears the email field and any error message in the impersonation form
    const handleResetForm = () => { setSettings({ Email: "" }); setError(undefined); };

    // Called when the support agent clicks "Exit Support Mode".
    // Resets all impersonation state and restores the agent's own language preferences.
    const handleDeactivate = () => {
        setImpersonationActive(false);
        setUserDetails({ email: "", country: "", workSite: "" }); // clear the impersonated user's details
        handleNewChatClick(); // start a fresh chat for the agent's own account
        loadingToastRef.current = toast.loading("Restoring your language preferences..."); // spinner shown until the preferred language is restored
        logEvent(
            { eventType: "Exit Support Mode", SupportedUser: appStateContext?.state.ImpersonatedUser },
            appStateContext?.state.loggedInUserCountry ?? "",
            appStateContext?.state.userGId,
            token,
            appStateContext?.state.impUserCountry ?? "",
        );
        appStateContext?.dispatch({ type: "IS_IMPERSONATING", payload: false });
        appStateContext?.dispatch({ type: "IMPERSONATED_USER", payload: null });
        appStateContext?.dispatch({ type: "IMP_USER_COUNTRY", payload: null });
    };

    // Updates a single field in the impersonation form when the user types
    const handleSettingsChange = (event: any) => {
        const { name, value } = event.target;
        setSettings(prev => ({ ...prev, [name]: value })); // merge the new field value into the existing form state
    };

    // ── Footer / Settings panel ──

    const isUserMenu = activePanel === "user-menu"; // true when the Settings or Languages panel is open

    // Builds the two footer menu items (Settings, Languages) with the correct open-panel actions
    const footerMenuItems = getFooterMenuItems(
        () => { setActivePanel("user-menu"); setPanelView("Settings"); },    // clicking Settings opens the Settings sub-view
        () => { setActivePanel("user-menu"); setPanelView("Languages"); },   // clicking Languages opens the language picker sub-view
        currentLanguageNativeLabel,                                           // shown next to the Languages item, e.g. 'עברית'
    );

    // Maps the sub-view name to the component that should be rendered inside the panel
    const subViewContent: Record<string, React.ReactNode> = {
        Settings: <SettingsPanel />,   // appearance and privacy settings
        Languages: <LanguagePanel />,  // full searchable language list
    };

        // Maps the sub-view name to the component that should be rendered inside the panel
    const panelTitle: Record<string, React.ReactNode> = {
        Settings: t("common.settings", {value:""}),   // appearance and privacy settings
        Languages: t("common.languages"),  // full searchable language list
    };


    // Called when the user accepts the terms and conditions in the consent popup.
    // Sends the accepted text to the server and stores the timestamp in global state.
    const handleAcceptsTerms = async () => {
        try {
            setLoading(true);
            let disclaimer = `${t("common.Disclaimer")}`;
            const response = await acceptUserConsent(token, disclaimer); // send the accepted disclaimer text to the backend
            if (!response.ok) {
                throw new Error("Failed to accept terms");
            }

            const data = await response.json();
            // Record the acceptance timestamp in global state so the popup is not shown again immediately
            appStateContext?.dispatch({
                type: "TERMS_ACCEPTED_AT",
                payload: data?.TermsAcceptedAt
            });
            setLoading(false);
            setConsentDialogOpen(false); // close the popup once the acceptance is confirmed
        } catch (error) {
            setLoading(false)
            console.error("Error accepting terms:", error);
        }
    }

    // ── Language Selection Handler ──

    // Called when the user picks a language from the consent popup's language dropdown.
    // Switches the app language immediately and saves the preference to the server.
    const handleLanguageChange = async (language: string) => {
        if (!language) return;
        const selectedCode = language;
        setSelectedLanguage(selectedCode); // update the dropdown to show the chosen language

        try {
            if (appStateContext?.state?.isImpersonating) {
                // Support agents — update state only, no API call
                appStateContext?.dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode });
            } else {
                // Normal user — save the preference to the server
                const response = await updateLanguage(
                    token,
                    { preferred_language: selectedCode, global_id: appStateContext?.state.userGId })
                if (!response.ok) {
                    console.error("Failed to update preferred language");
                } else {
                    appStateContext?.dispatch({ type: "USER_PREFERRED_LANGUAGE", payload: selectedCode });
                }
            }
        } catch (error) {
            console.error("Error while updating preferred language:", error);
        }
    };

    // The Teva logo rendered in the sidebar header
    const Logo = () => <img src={Teva} width={25} height={25} style={{ display: "block", flexShrink: 0 }} alt="AskHR logo" />;

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {/* Toast notification container — bottom-right on desktop, top-center on mobile; no close button */}
            {createPortal(
                <ToastContainer
                    position={isMobile ? "top-center" : "bottom-right"}
                    style={isMobile ? { top: "2%", left: "50%", transform: "translateX(-50%)", width: "98%" } : { bottom: "2%", right: "2%" }}
                    closeButton={false}
                />,
                document.body
            )}
            <TevaSidebarLayout activePanel={activePanel} onPanelClose={closePanel}>

                {/* ── Primary sidebar (left column) ── */}
                <TevaSidebarLayout.Primary>
                    <Sidebar
                        isPinned={isPinned}
                        onPinnedChange={handlePinnedChange}
                        forceCollapsed={Boolean(activePanel)}           // always collapse when a secondary panel is open
                        hamburgerPosition={isRTL(t("chat.newChatButton"))?{ top: 14, right:14 }: { top: 14, left:14 }}
                        secondaryPanelOpen={Boolean(activePanel)}
                        dir={i18n.language === "he" || i18n.language === "ar" ? "rtl" : "ltr"} // flip layout for Hebrew and Arabic
                    >
                        {/* Logo + app name; clicking starts a new chat */}
                        <Sidebar.Header logo={{ icon: <Logo /> }} appName="AskHR" onClick={handleNewChatClick} pinTooltip={t("layout.pinHelp")} unpinTooltip={t("layout.unPinHelp")} closeMenuTooltip={t("common.close")}/>

                        {/* "New Chat" button at the top of the sidebar */}
                        <Sidebar.NewChat
                            onClick={handleNewChatClick}
                            color={COLORS.brand.violet}
                            hoverColor={COLORS.violetEggplant[900]}
                            label={t("chat.newChatButton")}
                        />

                        <Sidebar.Body>
                            {/* Navigation icons: Chat History, and Support Mode for support staff */}
                            <SidebarNavItems
                                activePanel={activePanel}
                                isSupportUser={isSupportUser}
                                impersonationActive={impersonationActive}
                                onNavItemClick={(id) => {
                                    if (id === 'support-mode') {
                                        setActivePanel(p => p === 'support-mode' ? null : 'support-mode');
                                        return;
                                    }
                                    if (id === 'create-ticket') {
                                        handleOpenTicketClick();
                                        return;
                                    }
                                    const item = navItems.find(n => n.id === id);
                                    if (item?.openPanel) {
                                        setActivePanel(p => p === id ? null : id);
                                    }
                                }}
                            />
                        </Sidebar.Body>

                        {/* User avatar and footer menu (Settings, Languages) */}
                        <Sidebar.Footer
                            user={{
                                name: userName ?? "User",
                                email: userLoggedIn ?? "",
                            }}
                            menuItems={footerMenuItems}
                        />
                        {/* Invisible helper that closes the mobile drawer when triggered */}
                        <MobileDrawerCloser trigger={closeMobileTrigger} />
                    </Sidebar>
                </TevaSidebarLayout.Primary>

                {/* ── Secondary panel (right of sidebar — chat history, settings, support mode) ── */}
                <TevaSidebarLayout.Secondary
                    defaultWidth={320}
                    minWidth={200}
                    maxWidth={700}
                    isResizable={true}  // user can drag the panel edge to resize it
                >
                    {isUserMenu ? (
                        // Settings or Languages sub-view: shows a titled header and the appropriate panel body
                        <>
                            <TevaSidebarLayout.Secondary.Header title={panelTitle[panelView ?? ""] ?? undefined} onClose={closePanel} closeTooltip={t("common.close")}/>
                            <TevaSidebarLayout.Secondary.Body>
                                {panelView && subViewContent[panelView]} {/* renders either <SettingsPanel /> or <LanguagePanel /> */}
                            </TevaSidebarLayout.Secondary.Body>
                        </>
                    ) : (
                        // Chat History or Support Mode panel
                        <>
                            {/* Chat History manages its own header internally, so we skip it here */}
                            {activePanel !== "chat-history" && (
                                <TevaSidebarLayout.Secondary.Header
                                    title={activePanel === 'support-mode' ? 'Support Mode' : navItems.find(n => n.id === activePanel)?.label}
                                    onClose={closePanel}
                                />
                            )}
                            <TevaSidebarLayout.Secondary.Body>
                                {/* Placeholder for a future Workspaces panel */}
                                {activePanel === "support" && (
                                    <div className={mergeStyles(styles.workspaceContent)}>
                                        <p>Workspaces content</p>
                                    </div>
                                )}
                                {/* Full conversation history list */}
                                {activePanel === "chat-history" && (
                                    <ChatHistoryPanel onClose={closePanelFromChatHistory} />
                                )}
                                {/* Support Mode panel — lets support staff start/stop viewing the app as another user */}
                                {activePanel === "support-mode" && (
                                    <SupportModePanel
                                        isActive={impersonationActive}
                                        userDetails={userDetails}
                                        onDeactivate={handleDeactivate}
                                        onImpersonate={handleImpersonate}
                                        settings={settings}
                                        onSettingsChange={handleSettingsChange}
                                        error={error}
                                        onResetForm={handleResetForm}
                                        isImpersonateLoading={isImpersonateLoading}
                                    />
                                )}
                            </TevaSidebarLayout.Secondary.Body>
                        </>
                    )}
                </TevaSidebarLayout.Secondary>

                {/* ── Main content area: the active child route renders here ── */}
                <div className={mergeStyles(styles.mainContent)}>
                    <Outlet context={{ newChatRef, openTicketRef } satisfies OutletContext} />
                </div>

            </TevaSidebarLayout>

            {/* ── First-time login consent popup ── */}
            {/* Shown when the user has never accepted the terms, or when the acceptance has expired */}
            <DisclaimerPopup
                isOpen={isConsentDialogOpen}
                isLoading={isLoading}
                titleText={t("chat.welcomeMessage", {
                    userName: appStateContext?.state?.loggedInUserName?.firstName ?? "",
                    comma: appStateContext?.state?.loggedInUserName?.firstName ? ", " : "", // adds a comma after the name only when a name is present
                })}
                disclaimer={t("common.iUnderstand")}
                acceptBtnText={t("common.confirm")}
                dropdownSearchPlaceholder={t("common.search", { value: t("common.languages") })}
                dropdownNoResultText={t("common.noFound", { value: t("common.languages") })}
                onTermsAccept={handleAcceptsTerms}
                language={selectedLanguage}
                updateLanguage={handleLanguageChange}
                languageOptions={dropdownOptions}
                dir={i18n.language === "he" || i18n.language === "ar" ? "rtl" : "ltr"} // flip layout for Hebrew and Arabic
                showConsentCheckbox={false}
            >
                {/* Disclaimer body text shown inside the popup */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", textAlign: isRTL(t("common.learnMore")) ? 'right': 'left',
                            direction: isRTL(t("common.learnMore")) ? 'rtl': 'ltr' }}>
                    <span> {t("common.Disclaimer")}</span>
                    <a href="https://www.myteva.com/page/58263" target="_blank" tabIndex={-1}>{t("common.learnMore")}</a>
                </div>
            </DisclaimerPopup>
        </>
    );
}
